class IgniteIngredientsAccordion extends HTMLElement {
  constructor() {
    super();
    this.details = [];
    this.animations = new WeakMap();
    this.scrollAnchorFrame = null;
    this.scrollAnchorOverflow = null;
    this.handleSummaryClick = this.handleSummaryClick.bind(this);
    this.stopScrollAnchorTracking = this.stopScrollAnchorTracking.bind(this);
  }

  connectedCallback() {
    this.details = Array.from(this.querySelectorAll(":scope > details"));

    this.details.forEach((details) => {
      details.removeAttribute("name");
      details
        .querySelector(":scope > summary")
        ?.addEventListener("click", this.handleSummaryClick);
    });
  }

  disconnectedCallback() {
    this.details.forEach((details) => {
      details
        .querySelector(":scope > summary")
        ?.removeEventListener("click", this.handleSummaryClick);
      this.animations.get(details)?.cancel();
      this.resetStyles(details);
    });

    this.stopScrollAnchorTracking();
    this.details = [];
  }

  handleSummaryClick(event) {
    const summary = event.currentTarget;
    const details = summary.parentElement;

    if (!(details instanceof HTMLDetailsElement)) return;

    event.preventDefault();

    if (this.animations.has(details)) return;

    if (details.open) {
      this.animate(details, false);
      return;
    }

    const closingDetails = this.details.filter(
      (item) => item !== details && item.open && !this.animations.has(item)
    );

    const closesContentAbove = closingDetails.some(
      (item) =>
        item.compareDocumentPosition(details) & Node.DOCUMENT_POSITION_FOLLOWING
    );

    this.trackSummaryPosition(summary, closesContentAbove);

    closingDetails.forEach((item) => {
      this.animate(item, false);
    });

    this.animate(details, true);
  }

  trackSummaryPosition(summary, preservePosition) {
    this.stopScrollAnchorTracking();

    const anchorTop = preservePosition
      ? summary.getBoundingClientRect().top
      : null;
    this.scrollAnchorOverflow = this.style.getPropertyValue("overflow-anchor");
    this.style.setProperty("overflow-anchor", "none");

    const updateScrollPosition = () => {
      if (!summary.isConnected) {
        this.stopScrollAnchorTracking();
        return;
      }

      const delta =
        anchorTop === null
          ? 0
          : summary.getBoundingClientRect().top - anchorTop;

      if (Math.abs(delta) > 0.5) {
        window.scrollBy(0, delta);
      }

      if (this.details.some((item) => this.animations.has(item))) {
        this.scrollAnchorFrame = requestAnimationFrame(updateScrollPosition);
        return;
      }

      this.stopScrollAnchorTracking();
      this.scrollSummaryToTop(summary);
    };

    window.addEventListener("wheel", this.stopScrollAnchorTracking, {
      passive: true,
    });
    window.addEventListener("touchmove", this.stopScrollAnchorTracking, {
      passive: true,
    });
    this.scrollAnchorFrame = requestAnimationFrame(updateScrollPosition);
  }

  scrollSummaryToTop(summary) {
    const stickyHeader = document.querySelector("sticky-header");
    const announcementBar = document.querySelector(".announcement-bar-section");
    const hasStickyAnnouncement = announcementBar?.querySelector(
      ".announcement-bar-section__sticky"
    );
    const stickyOffset =
      (stickyHeader?.offsetHeight || 0) +
      (hasStickyAnnouncement ? announcementBar.offsetHeight : 0);
    const targetTop =
      window.scrollY + summary.getBoundingClientRect().top - stickyOffset;

    window.scrollTo({
      top: Math.max(0, targetTop),
      behavior: this.prefersReducedMotion() ? "auto" : "smooth",
    });
  }

  stopScrollAnchorTracking() {
    if (this.scrollAnchorFrame !== null) {
      cancelAnimationFrame(this.scrollAnchorFrame);
      this.scrollAnchorFrame = null;
    }

    window.removeEventListener("wheel", this.stopScrollAnchorTracking);
    window.removeEventListener("touchmove", this.stopScrollAnchorTracking);

    if (this.scrollAnchorOverflow !== null) {
      if (this.scrollAnchorOverflow) {
        this.style.setProperty("overflow-anchor", this.scrollAnchorOverflow);
      } else {
        this.style.removeProperty("overflow-anchor");
      }

      this.scrollAnchorOverflow = null;
    }
  }

  animate(details, isOpening) {
    const summary = details.querySelector(":scope > summary");
    const panel = details.querySelector(":scope > [data-accordion-panel]");

    if (
      !summary ||
      !panel ||
      typeof details.animate !== "function" ||
      this.prefersReducedMotion()
    ) {
      details.open = isOpening;
      return;
    }

    const startHeight = details.getBoundingClientRect().height;
    details.open = isOpening;
    const endHeight = details.getBoundingClientRect().height;

    if (!isOpening) details.open = true;

    details.style.height = `${startHeight}px`;
    details.style.overflow = "hidden";

    const heightAnimation = details.animate(
      { height: [`${startHeight}px`, `${endHeight}px`] },
      { duration: 320, easing: "cubic-bezier(0.22, 1, 0.36, 1)" }
    );

    panel.animate(
      isOpening
        ? [
            { opacity: 0, transform: "translateY(-8px)" },
            { opacity: 1, transform: "translateY(0)" },
          ]
        : [
            { opacity: 1, transform: "translateY(0)" },
            { opacity: 0, transform: "translateY(-8px)" },
          ],
      { duration: isOpening ? 260 : 180, easing: "ease-out", fill: "both" }
    );

    this.animations.set(details, heightAnimation);

    heightAnimation.addEventListener(
      "finish",
      () => {
        details.open = isOpening;
        this.animations.delete(details);
        this.resetStyles(details);
      },
      { once: true }
    );
  }

  prefersReducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  resetStyles(details) {
    details.style.removeProperty("height");
    details.style.removeProperty("overflow");

    const panel = details.querySelector(":scope > [data-accordion-panel]");
    panel?.getAnimations().forEach((animation) => animation.cancel());
  }
}

if (!customElements.get("ignite-ingredients-accordion")) {
  customElements.define(
    "ignite-ingredients-accordion",
    IgniteIngredientsAccordion
  );
}
